import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { AuthProvider } from '@/contexts/AuthContext';

// Layouts
const MainLayout = lazy(() => import('@/layouts/MainLayout'));
const AdminLayout = lazy(() => import('@/layouts/AdminLayout'));

// Pages
const Index = lazy(() => import('./pages/Index'));
const DonatePage = lazy(() => import('./pages/Donate'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Foundation = lazy(() => import('./pages/Foundation'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const Events = lazy(() => import('./pages/Events'));
const EventDetails = lazy(() => import('./pages/EventDetails'));
const Articles = lazy(() => import('./pages/Articles'));
const ArticleDetail = lazy(() => import('./pages/ArticleDetail'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const PatronPage = lazy(() => import('./pages/PatronPage'));
const PatronAchievementsPage = lazy(() => import('./pages/PatronAchievementsPage'));
const PatronTestimonialsPage = lazy(() => import('./pages/PatronTestimonialsPage'));
const Partnerships = lazy(() => import('./pages/Partnerships'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const MemorialPage = lazy(() => import('./pages/MemorialPage'));

// Admin Pages
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const ProjectsListPage = lazy(() => import('@/pages/admin/projects'));
const NewProjectPage = lazy(() => import('@/pages/admin/projects/new'));
const EditProjectPage = lazy(() => import('@/pages/admin/projects/[id]/edit'));
const ManageEvents = lazy(() => import('@/pages/admin/events'));
const EventForm = lazy(() => import('@/pages/admin/events/EventForm'));
const ArticlesList = lazy(() => import('@/pages/admin/articles'));
const ArticleForm = lazy(() => import('@/pages/admin/articles/ArticleForm'));
const ManageDonations = lazy(() => import('@/pages/admin/ManageDonations'));
const ManageUsers = lazy(() => import('@/pages/admin/ManageUsers'));
const Settings = lazy(() => import('@/pages/admin/Settings'));
const GalleryAdmin = lazy(() => import('@/pages/admin/gallery'));
const AdminPatronPage = lazy(() => import('@/pages/admin/PatronPage'));

// Protected Route Component
const ProtectedRoute = lazy(() => import('@/components/auth/ProtectedRoute'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (anciennement cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: 'always',
      retryOnMount: true,
      placeholderData: (previousData) => previousData, // Garde les données précédentes pendant le rechargement
    },
  },
});

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center space-y-4">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      <span className="text-muted-foreground">Chargement...</span>
    </div>
  </div>
);

// Composant pour initialiser les traductions
const I18nInitializer = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Forcer le chargement des traductions
    i18n.loadNamespaces(['common', 'header', 'auth']);
  }, [i18n]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <I18nInitializer />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={
                <MainLayout overlayHeader>
                  <Index />
                </MainLayout>
              } />
              <Route path="/donate" element={
                <MainLayout>
                  <DonatePage />
                </MainLayout>
              } />
              <Route path="/foundation" element={
                <MainLayout>
                  <AboutUs />
                </MainLayout>
              } />
              <Route path="/qui-sommes-nous" element={
                <MainLayout>
                  <AboutUs />
                </MainLayout>
              } />
              <Route path="/about" element={<Navigate to="/qui-sommes-nous" replace />} />
              <Route path="/projects" element={
                <MainLayout>
                  <Projects />
                </MainLayout>
              } />
              <Route path="/projects/:slug" element={
                <MainLayout>
                  <ProjectDetails />
                </MainLayout>
              } />
              <Route path="/events" element={
                <MainLayout>
                  <Events />
                </MainLayout>
              } />
              <Route path="/events/:id" element={
                <MainLayout>
                  <EventDetails />
                </MainLayout>
              } />
              <Route path="/gallery" element={
                <MainLayout>
                  <Gallery />
                </MainLayout>
              } />
              <Route path="/articles" element={
                <MainLayout>
                  <Articles />
                </MainLayout>
              } />
              <Route path="/articles/:slug" element={
                <MainLayout>
                  <ArticleDetail />
                </MainLayout>
              } />
              <Route path="/contact" element={
                <MainLayout>
                  <Contact />
                </MainLayout>
              } />
              <Route path="/parrain">
                <Route index element={
                  <MainLayout>
                    <PatronPage />
                  </MainLayout>
                } />
                <Route
                  path="oeuvres"
                  element={
                    <MainLayout>
                      <PatronAchievementsPage />
                    </MainLayout>
                  }
                />
                <Route path="realisations" element={<Navigate to="../oeuvres" replace />} />
                {/* Alias pour la version anglaise */}
                <Route
                  path="achievements"
                  element={<Navigate to="../oeuvres" replace />}
                />
                {/* Routes pour les onglets */}
                <Route
                  path="biography"
                  element={
                    <MainLayout>
                      <PatronPage />
                    </MainLayout>
                  }
                />
                <Route
                  path="temoignages"
                  element={
                    <MainLayout>
                      <PatronTestimonialsPage />
                    </MainLayout>
                  }
                />
                {/* Alias pour la version anglaise */}
                <Route
                  path="testimonials"
                  element={
                    <MainLayout>
                      <PatronTestimonialsPage />
                    </MainLayout>
                  }
                />
                <Route
                  path="media"
                  element={
                    <MainLayout>
                      <PatronPage />
                    </MainLayout>
                  }
                />
                <Route
                  path="links"
                  element={
                    <MainLayout>
                      <PatronPage />
                    </MainLayout>
                  }
                />
              </Route>
              <Route path="/partenariats" element={
                <MainLayout>
                  <Partnerships />
                </MainLayout>
              } />
              <Route path="/memorial" element={
                <MainLayout>
                  <MemorialPage />
                </MainLayout>
              } />

              {/* Auth Routes */}
              <Route path="/login" element={
                <MainLayout>
                  <Login />
                </MainLayout>
              } />
              <Route path="/register" element={
                <MainLayout>
                  <Register />
                </MainLayout>
              } />
              <Route path="/forgot-password" element={
                <MainLayout>
                  <ForgotPassword />
                </MainLayout>
              } />
              <Route path="/reset-password" element={
                <MainLayout>
                  <ResetPassword />
                </MainLayout>
              } />

              {/* Admin Layout Route */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole="editor">
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />

                {/* Routes des projets */}
                <Route path="projects">
                  <Route index element={<ProjectsListPage />} />
                  <Route path="new" element={<NewProjectPage />} />
                  <Route path=":id/edit" element={<EditProjectPage />} />
                </Route>

                {/* Routes des articles */}
                <Route path="articles">
                  <Route index element={<ArticlesList />} />
                  <Route path="new" element={<ArticleForm />} />
                  <Route path="edit/:id" element={<ArticleForm />} />
                </Route>

                {/* Routes des événements */}
                <Route path="events">
                  <Route index element={<ManageEvents />} />
                  <Route path="new" element={<EventForm />} />
                  <Route path="edit/:id" element={<EventForm />} />
                </Route>

                <Route path="donations" element={<ManageDonations />} />

                {/* Route pour la galerie d'administration */}
                <Route
                  path="gallery"
                  element={
                    <ProtectedRoute requiredRole="editor">
                      <GalleryAdmin />
                    </ProtectedRoute>
                  }
                />

                {/* Routes protégées par le rôle admin */}
                <Route
                  path="users"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <ManageUsers />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="settings"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <Settings />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="patron"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminPatronPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* 404 - Keep this as the last route */}
              <Route path="*" element={
                <MainLayout>
                  <NotFound />
                </MainLayout>
              } />
            </Routes>
          </Suspense>
          <Toaster />
          <Sonner position="top-center" />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
